import { CrownFrame } from "@/components/CrownFrame";
import { useCallback, useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Swords, Skull, Tent, HelpCircle, Crown, Lock, Leaf, Crosshair, Check } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { useGame } from "@/context/GameContext";
import { Bar } from "@/components/Bar";
import BoostBadges from "@/components/BoostBadges";
import Tutorial from "@/components/Tutorial";
import EventDialog from "@/components/EventDialog";
import RegionNav from "@/components/RegionNav";
import EnergyPanel from "@/components/EnergyPanel";
import DailyReward from "@/components/DailyReward";
import TodayPanel from "@/components/TodayPanel";
import { celebrate } from "@/lib/daily";

const ICONS = { battle: Swords, elite: Skull, den: Tent, event: HelpCircle, boss: Crown };
const TYPE_LABEL = { battle: "Battle", elite: "Elite battle", den: "Rest den", event: "Mystery event", boss: "Region boss" };

function MapNode({ n, onTravel }) {
  const Icon = n.status === "locked" ? Lock : n.status === "cleared" ? Check : ICONS[n.type];
  const styles = {
    available: "bg-fuchsia-500 text-slate-950 border-fuchsia-200 node-pulse cursor-pointer hover:scale-110",
    current: "bg-sky-400 text-slate-950 border-sky-100 shadow-[0_0_20px_rgba(56,189,248,.7)]",
    cleared: "bg-slate-800 text-emerald-300 border-emerald-500/60",
    locked: "bg-slate-900/90 text-slate-500 border-slate-700",
  }[n.status];
  const big = n.type === "boss" ? "w-16 h-16" : "w-12 h-12";
  return (
    <button data-testid={`map-node-${n.id}`} data-status={n.status} disabled={n.status !== "available"} onClick={() => onTravel(n)}
      className="absolute -translate-x-1/2 -translate-y-1/2 flex flex-col items-center group" style={{ left: `${n.x}%`, top: `${n.y}%` }}>
      <span className={`${big} rounded-full border-2 grid place-items-center transition-transform ${styles}`}><Icon className="w-5 h-5" /></span>
      <span className={`mt-1.5 px-2 py-0.5 rounded bg-slate-950/85 text-[10px] sm:text-xs whitespace-nowrap text-slate-200 border border-slate-800 ${n.status === "available" || n.status === "current" ? "" : "hidden sm:inline"}`}>
        {n.name}{n.enemy_name && n.type !== "boss" ? ` · ${n.enemy_name}` : ""}
      </span>
      <span className="hidden group-hover:block absolute -top-7 text-[10px] font-mono-game text-fuchsia-300 whitespace-nowrap">{TYPE_LABEL[n.type]}</span>
    </button>
  );
}

function Paths({ nodes }) {
  const byId = Object.fromEntries(nodes.map((n) => [n.id, n]));
  return (
    <svg className="absolute inset-0 w-full h-full pointer-events-none" viewBox="0 0 100 100" preserveAspectRatio="none">
      {nodes.flatMap((n) => n.next.map((t) => {
        const m = byId[t];
        const lit = n.status === "cleared" || n.status === "current";
        return <line key={n.id + t} x1={n.x} y1={n.y} x2={m.x} y2={m.y} stroke={lit ? "#D946EF" : "#cbd5e1"} strokeOpacity={lit ? 1 : 0.35} strokeWidth={lit ? 0.6 : 0.5} strokeDasharray="6 5" vectorEffect="non-scaling-stroke" style={{ strokeWidth: lit ? 3 : 2 }} />;
      }))}
    </svg>
  );
}

export default function Journey() {
  const { game, refresh } = useGame();
  const nav = useNavigate();
  const [viewIdx, setViewIdx] = useState(game.wolf.region_idx);
  const [viewed, setViewed] = useState(null);
  const [eventOpen, setEventOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const { wolf, stats, boosts, settings } = game;
  const isCurrent = viewIdx === wolf.region_idx;
  const r = isCurrent ? game.region : viewed;

  useEffect(() => {
    if (isCurrent) return setViewed(null);
    let live = true;
    api.get(`/game/regions/${viewIdx}`).then(({ data }) => live && setViewed(data)).catch(() => {});
    return () => { live = false; };
  }, [viewIdx, isCurrent]);

  const goRegion = async (idx) => {
    setBusy(true);
    try { const { data } = await api.post("/game/region", { region_idx: idx }); toast.success(data.message); await refresh(); setViewIdx(idx); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };

  useEffect(() => { if (wolf.pending_event) setEventOpen(true); }, [wolf.pending_event]);
  const closeEvent = useCallback(() => setEventOpen(false), []);

  const travel = async (n) => {
    setBusy(true);
    try {
      const { data } = await api.post("/game/travel", { node_id: n.id });
      if (data.kind === "battle") return nav(`/battle/${data.battle_id}`);
      if (data.kind === "den") toast.success(data.message);
      await refresh();
      if (data.kind === "event") setEventOpen(true);
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  const hunt = async () => {
    try { const { data } = await api.post("/game/hunt"); nav(`/battle/${data.battle_id}`); } catch (e) { toast.error(errMsg(e)); }
  };
  const herb = async () => {
    try { const { data } = await api.post("/game/use-item", { item: "herb" }); await refresh(); toast.success("Herb eaten. Health restored."); celebrate(data.quests_done); } catch (e) { toast.error(errMsg(e)); }
  };

  return (
    <main className="max-w-7xl mx-auto px-4 sm:px-6 py-8 grid lg:grid-cols-[1fr_320px] gap-6">
      <Tutorial open={!wolf.tutorial_done} onDone={refresh} />
      <EventDialog open={eventOpen} onClose={closeEvent} rerollCost={game.reroll_cost} refresh={refresh} />
      <section>
        <RegionNav game={game} viewIdx={viewIdx} setViewIdx={setViewIdx} region={r} />
        <div data-testid="journey-map" className="relative w-full aspect-[4/3] sm:aspect-[3/2] rounded-2xl overflow-hidden border border-slate-800 bg-slate-950 grain">
          {!r ? <div className="absolute inset-0 grid place-items-center text-slate-500">Scouting the trail…</div> : r.playable ? (
            <>
              <img src={r.image} alt={`${r.name} map`} className="absolute inset-0 w-full h-full object-cover opacity-70" />
              <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,transparent_40%,#061016_95%)]" />
              <Paths nodes={r.nodes} />
              {r.nodes.map((n) => <MapNode key={n.id} n={n} onTravel={busy ? () => {} : travel} />)}
              {r.current && wolf.region_cleared && (
                <div data-testid="region-cleared-banner" className="absolute bottom-3 sm:bottom-auto sm:top-4 left-1/2 -translate-x-1/2 w-[92%] sm:w-auto flex flex-col sm:flex-row items-center gap-2 px-4 py-2 rounded-2xl sm:rounded-full bg-amber-500/90 text-slate-950 font-display font-bold text-sm text-center">
                  {r.idx === game.total_regions ? "The Shadowfang King has fallen. The forest is free!" : `${r.boss_name?.split(",")[0]} has fallen`}
                  {r.idx < game.total_regions && <button data-testid="banner-travel-next" disabled={busy} onClick={() => goRegion(r.idx + 1)} className="px-3 py-1 rounded-full bg-slate-950 text-amber-200 text-xs font-sans">On to Region {r.idx + 1} →</button>}
                </div>
              )}
              {!r.current && (
                <div className="absolute inset-0 grid place-items-center bg-[#061016]/60 backdrop-blur-[2px]">
                  <div className="text-center p-6">
                    <p className="font-display text-3xl text-white mb-2">{r.name}</p>
                    <p className="text-slate-300 text-sm mb-5">{r.cleared ? `You freed this region. Travel back to face ${r.boss_name?.split(",")[0]} again (your progress here restarts).` : `${r.boss_name} rules here. Travelling here starts the region from its entrance.`}</p>
                    <button data-testid="travel-region-btn" disabled={busy} onClick={() => goRegion(r.idx)} className="ember-btn px-6 py-3 rounded-full text-sm">TRAVEL TO REGION {r.idx}</button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div data-testid="region-locked" className="absolute inset-0 grid place-items-center text-center p-6">
              <img src={r.image} alt="" className="absolute inset-0 w-full h-full object-cover opacity-15 blur-sm" />
              <div className="relative"><Lock className="w-10 h-10 mx-auto text-slate-600 mb-4" /><p className="font-display text-2xl text-slate-300">{r.name} is shrouded in fog</p>
                <p className="text-slate-500 mt-2 text-sm">Clear Region {r.idx - 1} to open the path.{r.idx === game.total_regions ? " The Shadowfang King waits at the summit." : ""}</p></div>
            </div>
          )}
        </div>
      </section>

      <aside className="space-y-4">
        <div className="glass rounded-2xl p-5" data-testid="journey-wolf-panel">
          <div className="flex items-center gap-4 mb-4">
            <CrownFrame tier={game.wallet?.mwp?.tier} small className="rounded-xl"><img src={game.avatar} alt={wolf.name} className="w-16 h-16 rounded-xl object-cover border border-slate-700" /></CrownFrame>
            <div>
              <p className="font-display text-xl text-amber-100" data-testid="journey-wolf-name">{wolf.name}</p>
              {wolf.character && <p className="text-[11px] text-amber-300/90 truncate max-w-[160px]" data-testid="journey-character-name">as {wolf.character.name}</p>}
              <p className="text-xs font-mono-game text-slate-400">LEVEL <span data-testid="journey-wolf-level">{wolf.level}</span></p>
            </div>
          </div>
          <div className="space-y-3">
            <Bar testid="journey-hp-bar" label="HEALTH" value={wolf.hp} max={stats.hp} color="#22C55E" />
            <Bar testid="journey-xp-bar" label="XP" value={wolf.xp} max={wolf.xp_needed} color="#38BDF8" height="h-2" />
          </div>
          <div className="mt-4"><BoostBadges boosts={boosts} settings={settings} art={game.badge_art} compact /></div>
        </div>
        {wolf.active_battle && (
          <button data-testid="resume-battle-btn" onClick={() => nav(`/battle/${wolf.active_battle}`)} className="ember-btn w-full py-3 rounded-2xl text-sm">
            RESUME YOUR BATTLE
          </button>
        )}
        <div className="glass rounded-2xl p-5 space-y-3">
          {wolf.tutorial_done && <DailyReward refresh={refresh} />}
          <EnergyPanel game={game} refresh={refresh} />
          <button data-testid="hunt-btn" onClick={hunt} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-slate-700 hover:border-fuchsia-500 hover:bg-fuchsia-500/10 transition-colors text-sm">
            <Crosshair className="w-4 h-4 text-fuchsia-400" /> Wander & hunt (1 energy)
          </button>
          <button data-testid="use-herb-btn" onClick={herb} disabled={!wolf.inventory.herb} className="w-full flex items-center justify-center gap-2 py-3 rounded-lg border border-slate-700 hover:border-emerald-500 hover:bg-emerald-500/10 transition-colors text-sm disabled:opacity-40">
            <Leaf className="w-4 h-4 text-emerald-400" /> Eat herb ({wolf.inventory.herb})
          </button>
          <p className="text-xs text-slate-500 leading-relaxed">Glowing stops are open. Each fight or event costs 1 energy; dens are free, heal you fully and become your checkpoint.</p>
          {!game.wallet && <Link to="/wallet" data-testid="journey-connect-wallet-link" className="block text-xs text-amber-300 hover:text-amber-200">Hold our NFTs? Connect a wallet for permanent boosts →</Link>}
        </div>
        {wolf.tutorial_done && <TodayPanel refresh={refresh} />}
      </aside>
    </main>
  );
}
